import { createFileRoute } from "@tanstack/react-router";
import { canonicalLinks, ogUrl, ogImage, jsonLd, breadcrumbSchema, webPageSchema } from "@/lib/seo";

export const Route = createFileRoute("/privacy")({
  head: () => ({
    meta: [
      { title: "Privacy Policy: How Ajility Handles Your Data | Ajility" },
      {
        name: "description",
        content:
          "How Ajility collects, uses, stores and discloses personal information, including third-party services, retention, access rights and privacy complaints.",
      },
      { property: "og:title", content: "Privacy Policy: How Ajility Handles Your Data | Ajility" },
      {
        property: "og:description",
        content:
          "How Ajility collects, uses, stores and discloses personal information, including third-party services, retention, access rights and privacy complaints.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:title", content: "Privacy Policy: How Ajility Handles Your Data | Ajility" },
      { name: "twitter:description", content: "How Ajility collects, uses, stores and discloses personal information, including third-party services, retention, access rights and privacy complaints." },
      ...ogImage("privacy", "Ajility privacy policy"),
      ogUrl("/privacy"),
    ],
    links: canonicalLinks("/privacy"),
    scripts: [
      jsonLd(breadcrumbSchema([{ name: "Privacy", path: "/privacy" }])),
      jsonLd(webPageSchema({ image: "privacy",
        path: "/privacy",
        name: "Privacy Policy: How Ajility Handles Your Data | Ajility",
        description: "How Ajility collects, uses, stores and discloses personal information, including third-party services, retention, access rights and privacy complaints.",
      })),
    ],
  }),
  component: PrivacyPage,
});

function PrivacyPage() {
  return (
    <main className="bg-surface pb-24 pt-32">
      <div className="mx-auto max-w-3xl px-6">
        <h1 className="font-serif text-4xl text-midnight md:text-5xl">Privacy Policy</h1>
        <p className="mt-4 text-sm text-charcoal">Last updated: 30 October 2026</p>
        <p className="mt-6 text-charcoal">
          Ajility respects your privacy. This policy explains how we collect, use,
          disclose and protect personal information, and how the Australian Privacy
          Principles apply to us. Ajility is a trading name of AJILITY PTY LTD, ABN 39 649 782
          427.
        </p>

        <h2 className="mt-10 font-serif text-2xl text-midnight">Information we collect</h2>
        <p className="mt-3 text-charcoal">
          We collect contact details, business information and any other details you
          provide when enquiring about our services, requesting pro bono support,
          booking a fit call or completing the Operating Scan or using one of the free tools on this site. We also collect basic
          technical and usage data through website analytics.
        </p>

        <h2 className="mt-10 font-serif text-2xl text-midnight">How we use it</h2>
        <p className="mt-3 text-charcoal">
          We use your information to respond to enquiries, schedule meetings, deliver
          our services and meet our legal obligations. We do not sell your data, and we
          do not share it with third parties for their own marketing.
        </p>

        <h2 className="mt-10 font-serif text-2xl text-midnight">Third-party services</h2>
        <p className="mt-3 text-charcoal">
          The following services collect or store information when you use this
          site:
        </p>
        <ul className="mt-3 list-disc space-y-3 pl-6 text-charcoal">
          <li>
            <strong>Operating Scan.</strong> The scan runs on
            diagnostic.ajility.com.au, a subdomain we operate. Answers you enter
            into the scan are submitted to and stored by that application, not on this
            website, and its operator's own privacy terms apply in addition to this
            policy.
          </li>
          <li>
            <strong>Calendly.</strong> The booking widget embedded on our contact page
            is provided by Calendly. When it loads, Calendly may collect technical data,
            and any name, email address and booking details you enter are collected and
            stored by Calendly.
          </li>
          <li>
            <strong>Google Analytics.</strong> If you accept analytics, we use Google
            Analytics to measure website usage. Google collects technical data such as
            pages viewed, approximate location and device information. Analytics does not
            load and no data is sent to Google unless you accept.
          </li>
          <li>
            <strong>Supabase.</strong> Enquiry form and charity support form submissions
            are stored in a database hosted by Supabase on our behalf.
          </li>
          <li>
            <strong>Free tools.</strong> When you unlock a tool report we collect your first name, work email, mobile number and, if you add it, your business name, together with the figures you entered, and store them in our database hosted by Supabase. We use them to send your report and to contact you about it. If you tick the insights box we will also send you occasional Ajility insights by email, and every email tells you how to unsubscribe. Report emails are sent through our email provider. The Partner Alignment Check partner code holds only your first name, the role you selected and your 18 ratings; it is created and compared in your browser and is never sent to us.
          </li>
        </ul>

        <h2 className="mt-10 font-serif text-2xl text-midnight">
          Analytics and session recording
        </h2>
        <p className="mt-3 text-charcoal">
          With your consent, we use Google Analytics 4 and PostHog to understand how this
          site is used, such as which pages are visited and which links are followed.
          PostHog may also record how you interact with pages, including clicks, scrolling
          and navigation. Anything you type into a form is masked, so we do not see the
          content of form fields.
        </p>
        <p className="mt-3 text-charcoal">
          PostHog stores this information on servers in the United States, so by giving
          consent you agree to that overseas disclosure under Australian Privacy Principle
          8. Google Analytics data is also processed overseas. Neither tool loads and no
          data is sent until you accept.
        </p>
        <p className="mt-3 text-charcoal">
          You can withdraw your consent at any time using the cookie settings link in the
          footer of every page, which reopens the consent choice.
        </p>

        <h2 className="mt-10 font-serif text-2xl text-midnight">Overseas disclosure</h2>
        <p className="mt-3 text-charcoal">
          Under Australian Privacy Principle 8, we tell you when personal information may
          be disclosed to overseas recipients. The providers named above are located
          outside Australia or may store or process data outside Australia, including in
          the United States and other countries where their infrastructure operates. By
          submitting information through those services you consent to that disclosure.
          Where practicable we take reasonable steps to ensure recipients handle your
          information consistently with the Australian Privacy Principles.
        </p>

        <h2 className="mt-10 font-serif text-2xl text-midnight">Retention</h2>
        <p className="mt-3 text-charcoal">
          We keep enquiry and scan-related information for as long as needed for the
          purpose it was collected, and then for seven years where records relate to a
          paid engagement, so that we can meet tax and business record obligations.
          Information that is no longer needed and that we are not required to keep is
          deleted or de-identified.
        </p>

        <h2 className="mt-10 font-serif text-2xl text-midnight">Access and correction</h2>
        <p className="mt-3 text-charcoal">
          Under Australian Privacy Principles 12 and 13 you may request access to the
          personal information we hold about you and ask us to correct it if it is
          inaccurate, out of date, incomplete, irrelevant or misleading. Email{" "}
          <a href="mailto:adam@ajility.com.au" className="underline hover:text-midnight-deep">
            adam@ajility.com.au
          </a>{" "}
          and we will respond within 30 days. If we refuse access or correction, we will
          explain why in writing.
        </p>

        <h2 className="mt-10 font-serif text-2xl text-midnight">Complaints</h2>
        <p className="mt-3 text-charcoal">
          If you believe we have mishandled your personal information, email{" "}
          <a href="mailto:adam@ajility.com.au" className="underline hover:text-midnight-deep">
            adam@ajility.com.au
          </a>{" "}
          with the details. We will acknowledge your complaint within five business days
          and respond within 30 days. If you are not satisfied with our response, you may
          complain to the Office of the Australian Information Commissioner at{" "}
          <a
            href="https://www.oaic.gov.au"
            className="underline hover:text-midnight-deep"
            target="_blank"
            rel="noopener noreferrer"
          >
            oaic.gov.au
          </a>{" "}
          or on 1300 363 992.
        </p>

        <h2 className="mt-10 font-serif text-2xl text-midnight">Contact</h2>
        <p className="mt-3 text-charcoal">
          For privacy questions, email{" "}
          <a href="mailto:adam@ajility.com.au" className="underline hover:text-midnight-deep">
            adam@ajility.com.au
          </a>
          .
        </p>
      </div>
    </main>
  );
}
